import { cloneElement, useEffect, useRef } from "react";
import newId from "../../../lib/id-utils";
import IconButton from "../IconButton/IconButton";
import styles from "./dialog.css";

/**
 * CustomDialog is a component that renders a custom dialog box with a title, content, and buttons.
 * The open state is controlled by the parent component.
 *
 * @param {Object} param - The parameter object.
 * @param {boolean} param.open - Indicates whether the dialog is open or not.
 * @param {function} param.setOpen - Callback function to update the open state of the dialog.
 * @param {string} param.title - The title of the dialog.
 * @param {ReactNode} param.children - The content of the dialog.
 * @param {JSX.Element[]} param.buttons - An array of buttons to be displayed in the dialog footer.
 * @param {boolean} [param.withButtonClose=true] - Whether to render the close (X) button. When false the dialog is also non-dismissable via Escape.
 * @param {boolean} [param.isDarkMode=true] - Whether to render the dialog in dark mode.
 * @return {JSX.Element} - The rendered dialog component.
 */
function Dialog({
  open,
  setOpen,
  title,
  children,
  buttons,
  withButtonClose = true,
  isDarkMode = true,
}) {
  const id = useRef(newId());
  const dialogRef = useRef(null);
  const footerRef = useRef(null);

  // Control the visibility depending on the open parameter.
  useEffect(() => {
    if (dialogRef.current === null) return;

    if (open) {
      dialogRef.current.showModal();

      // Prefer the first input/textarea so the user can start typing right away.
      // Otherwise focus the primary action (last button in the footer) rather
      // than the close button — the confirm action conventionally comes last
      // (see getConfirmAbortButtons: [abort, confirm]).
      const field = dialogRef.current.querySelector("input, textarea");
      if (field) {
        field.focus();
      } else {
        const footerButtons = footerRef.current?.querySelectorAll("button");
        footerButtons?.[footerButtons.length - 1]?.focus();
      }
    } else {
      dialogRef.current.close();
    }
  }, [open]);

  // Escape handling for the native <dialog>. A closable dialog mirrors the
  // dismissal back into React state so `open` stays in sync. A non-closable one
  // must not be dismissable at all, which takes three layers because no single
  // one is enough across browsers:
  //   1. swallow the Escape keydown (Firefox closes as the key's default action);
  //   2. preventDefault the `cancel` event (the standard hook);
  //   3. re-open on `close` as a catch-all — Chromium's close-watcher anti-trap
  //      heuristic forces the second Escape through regardless of 1 & 2.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    const onKeyDown = (e) => {
      if (!withButtonClose && e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onCancel = (e) => {
      if (!withButtonClose) e.preventDefault();
    };
    const onClose = () => {
      if (!withButtonClose) {
        if (open) dialog.showModal();
      } else {
        setOpen?.(false);
      }
    };

    dialog.addEventListener("keydown", onKeyDown);
    dialog.addEventListener("cancel", onCancel);
    dialog.addEventListener("close", onClose);
    return () => {
      dialog.removeEventListener("keydown", onKeyDown);
      dialog.removeEventListener("cancel", onCancel);
      dialog.removeEventListener("close", onClose);
    };
  }, [open, withButtonClose, setOpen]);

  const labelId = `${id.current}-dialog-label`;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog({
        isDarkMode: isDarkMode,
        // A dialog with no close affordance blocks the flow, so hide what's
        // behind it instead of the default translucent scrim.
        blockingBackdrop: !withButtonClose,
      })}
      aria-labelledby={labelId}
    >
      <div className={styles.dialogHeader({ isDarkMode: isDarkMode })}>
        <h1
          className={styles.dialogTitle({ isDarkMode: isDarkMode })}
          id={labelId}
        >
          {title}
        </h1>

        {withButtonClose && (
          <div className={styles.buttonClose}>
            <IconButton
              value="schliessen"
              name="xmark"
              onClick={() => setOpen(false)}
              circle
            />
          </div>
        )}
      </div>

      <div className={styles.dialogContent}>
        {children}

        <div
          className={styles.dialogFooter({ isDarkMode: isDarkMode })}
          ref={footerRef}
        >
          {buttons.map((button, i) => cloneElement(button, { key: i }))}
        </div>
      </div>
    </dialog>
  );
}

export default Dialog;
